import React from 'react';
import { interpolate, Easing } from 'remotion';
import { SpeakerLabelTransitionEffect } from '@goldcast/player-config/src/types/transitions';

export interface TransitionSpeakerLabelOutParams {
  fps: number;
  frame: number;
  animationType: SpeakerLabelTransitionEffect;
  hideAfter: number;
  showAfter?: number;
  duration?: number;
}

/**
 * Generates CSS styles for speaker label exit animations based on speaker-relative timing.
 *
 * Animation Timeline (Speaker-Relative):
 * - frame: Frames since THIS SPEAKER started speaking
 * - hideAfter: Duration in seconds to stay fully visible before hiding
 * - BEFORE hideAfter: returns empty object (element is visible)
 * - DURING exit animation (hideFrame → hideFrame + animationDuration): animated exit
 * - AFTER exit: completely hidden (opacity: 0)
 *
 * @param params Animation parameters
 * @param params.fps Frames per second
 * @param params.frame Speaker-relative frame number
 * @param params.animationType Type of animation effect
 * @param params.hideAfter Duration in seconds to stay fully visible before hiding
 * @param params.showAfter Time when label started showing (used to calculate when it became fully visible)
 * @param params.duration Animation duration in seconds (default: 0.3)
 * @returns CSS properties for the current frame
 */
export function getTransitionSpeakerLabelOutStyles({
  fps,
  frame,
  animationType,
  hideAfter,
  showAfter = 0,
  duration = 0.3
}: TransitionSpeakerLabelOutParams): React.CSSProperties {
  // Calculate when label became visible (showAfter + entrance animation duration)
  const showFrame = Math.floor(showAfter * fps);
  const animationDuration = Math.ceil(duration * fps);
  const labelVisibleFrame = showFrame + animationDuration;

  // hideAfter is duration to stay visible AFTER becoming fully visible (not absolute time)
  const hideFrame = labelVisibleFrame + Math.floor(hideAfter * fps);

  // BEFORE hideAfter: element is visible (no styles applied)
  if (frame < hideFrame) {
    return {};
  }

  // Handle None animation type: instant hide without animation
  if (animationType === SpeakerLabelTransitionEffect.None) {
    // Instantly hidden after hideAfter
    return { opacity: 0, pointerEvents: 'none' };
  }

  // DURING exit animation (hideFrame → hideFrame + animationDuration)
  if (frame < hideFrame + animationDuration) {
    const progress = interpolate(frame, [hideFrame, hideFrame + animationDuration], [0, 1], {
      easing: Easing.in(Easing.ease),
      extrapolateRight: 'clamp'
    });

    switch (animationType) {
      case SpeakerLabelTransitionEffect.Fade:
        return { opacity: 1 - progress };

      case SpeakerLabelTransitionEffect.MoveLeft: {
        const translateX = interpolate(progress, [0, 1], [0, -110]);
        return { transform: `translateX(${translateX}%)`, opacity: 1 - progress };
      }

      case SpeakerLabelTransitionEffect.MoveBottom: {
        const translateY = interpolate(progress, [0, 1], [0, 110]);
        return { transform: `translateY(${translateY}%)`, opacity: 1 - progress };
      }

      case SpeakerLabelTransitionEffect.Scale: {
        const scale = interpolate(progress, [0, 1], [1, 0.5]);
        return { transform: `scale(${scale})`, opacity: 1 - progress };
      }

      case SpeakerLabelTransitionEffect.Mask: {
        const clipPath = `inset(0 ${progress * 100}% 0 0)`;
        return { clipPath, opacity: 1 };
      }

      default:
        return {};
    }
  }

  // AFTER exit animation: completely hidden
  return { opacity: 0, pointerEvents: 'none' };
}
